import type { InfiniteData, QueryFunctionContext, QueryKey } from '@tanstack/react-query'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

interface UseInfiniteScrollQueryOptions<TData, TPageParam = number> {
  queryKey: QueryKey
  queryFn: (context: QueryFunctionContext<QueryKey, TPageParam>) => Promise<TData>
  getNextPageParam: (lastPage: TData, pages: TData[]) => TPageParam | undefined
  placeholderData?: (previousData: InfiniteData<TData, TPageParam> | undefined) => InfiniteData<TData, TPageParam> | undefined
  initialPageParam?: TPageParam
  enabled?: boolean
}

export function useInfiniteScrollQuery<TData, TPageParam = number>({
  queryKey,
  queryFn,
  getNextPageParam,
  enabled = true,
  placeholderData,
  initialPageParam = 0 as TPageParam,
}: UseInfiniteScrollQueryOptions<TData, TPageParam>) {
  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isError,
    isFetchNextPageError,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn,
    getNextPageParam,
    enabled,
    placeholderData,
    initialPageParam,
  })

  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const pageCount = data?.pages.length ?? 0

  const hasNextPageRef = useRef(hasNextPage)
  const isFetchingNextPageRef = useRef(isFetchingNextPage)
  const isFetchNextPageErrorRef = useRef(isFetchNextPageError)

  useEffect(() => {
    hasNextPageRef.current = hasNextPage
  }, [hasNextPage])

  useEffect(() => {
    isFetchingNextPageRef.current = isFetchingNextPage
  }, [isFetchingNextPage])

  useEffect(() => {
    isFetchNextPageErrorRef.current = isFetchNextPageError
  }, [isFetchNextPageError])

  useEffect(() => {
    if (!enabled)
      return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPageRef.current && !isFetchingNextPageRef.current && !isFetchNextPageErrorRef.current) {
          void fetchNextPage()
        }
      },
      { threshold: 0.1 },
    )

    const sentinel = sentinelRef.current
    if (sentinel)
      observer.observe(sentinel)

    return () => observer.disconnect()
  }, [enabled, fetchNextPage, pageCount])

  return {
    pages: data?.pages ?? [],
    sentinelRef,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isLoading,
    isError,
    isFetchNextPageError,
    fetchNextPage,
    refetch,
  }
}
